import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

const SECTION_ID_PREFIX = 'shopify-section-';

function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

function getSection(sectionId) {
  return document.getElementById(`${SECTION_ID_PREFIX}${normalizeSectionId(sectionId)}`);
}

function isFastProductHeader(section) {
  const main = document.getElementById('MainContent');
  return !!(
    section?.querySelector('header-component') &&
    main?.dataset?.template?.includes('product-template')
  );
}

function waitForHeaderIntent(section, callback) {
  let done = false;
  let timer;
  const finish = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    section.removeEventListener('pointerdown', finish);
    section.removeEventListener('focusin', finish);
    callback();
  };
  section.addEventListener('pointerdown', finish, { passive: true });
  section.addEventListener('focusin', finish);
  timer = setTimeout(finish, 15000);
}

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Heavy renderer/morph modules are imported only when hydration is actually needed.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = getSection(normalizedId);

  if (!section || section.dataset.hydrated === 'true') return;

  const { sectionRenderer } = await import('@theme/section-renderer');
  await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });
  section.dataset.hydrated = 'true';
}

/**
 * Hydrates when the DOM is ready. On the bespoke NoxVeli product page, header
 * hydration waits for header intent (or a late fallback) so it cannot compete
 * with the hero image and initial interaction work.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    const section = getSection(sectionId);
    if (!section) return;

    if (isFastProductHeader(section)) {
      waitForHeaderIntent(section, () => requestIdleCallback(() => hydrateSection(sectionId, url)));
      return;
    }

    requestIdleCallback(() => hydrateSection(sectionId, url));
  });
}